import { useEffect, useState, useImperativeHandle, RefObject } from "react";
import { useQuery, useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from 'use-debounce';
import { Seller } from "@api/apiClient/sellers";
import { Tooltip } from '@components/new/Tooltip';
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { ConfirmEmail, ConfirmState } from "../ConfirmEmail";
import TextInputContainer from "@containers/TextInputContainer";
import SelectContainer from "@containers/SelectContainer";
import { linkSendCode } from "@api/apiClient";
import { validateYMKey, getPartnerId, createYMSeller, ICampaigns } from "@api/apiClient/sellers";
import PartnerIdInfoModal from "../PartnerIdInfoModal";
import { FormHandle } from "./types";
import { getErrorMessage } from "@utils/errorMessage";
import InfoIcon from '@assets/icons/outline/info.svg?react';
import styles from './YMForm.module.scss';

export interface YMFormType {
  email: string;
  apiKey: string;
  ymCampaignId: string;
  partnerId: string;
}

const StoresSelectValue = ({ item }: { item: ICampaigns | null }) => (
  <>{item?.name ?? ""}</>
)

enum steps {
  EMAIL = "email",
  CONFIRM = "confirm",
}

const YMForm = ({ formRef, onStatusChange, onSuccess, onError, seller, knownEmails }: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
    title?: string,
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  seller: Seller;
  knownEmails: string[];
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.EMAIL);
  const [ticket, setTicket] = useState("");
  const [pin, setPin] = useState("");
  const [confirmState, setConfirmState] = useState<ConfirmState>({ disabled: false, loading: false, });
  const [isPartnerInfoOpen, setPartnerInfoOpen] = useState(false);

  const { control, handleSubmit, watch, getValues, formState: { isValid }, setValue } = useForm<YMFormType>({
    defaultValues: {
      email: seller.email ?? "",
      apiKey: seller.ymSeller?.apiKey ?? "",
      ymCampaignId: seller.ymSeller?.ymCampaignId ?? "",
      partnerId: "",
    },
    mode: 'onBlur',
  });

  const ymCampaignId = watch("ymCampaignId");
  const apiKey = watch("apiKey");

  const sendLinkMutation = useMutation({
    mutationFn: linkSendCode,
  });

  const [apiKeyDebounced] = useDebounce(apiKey, 500);

  const getPartnerIdQuery = useQuery({
    queryKey: ["getPartnerId", ymCampaignId, apiKeyDebounced],
    queryFn: () => getPartnerId({ campaignId: ymCampaignId, apiKey: apiKeyDebounced }),
    retry: 1,
    enabled: Boolean(apiKeyDebounced && ymCampaignId),
    onSuccess: (data) => {
      setValue("partnerId", data.partnerId || "")
    },
  });

  const { data: validationData, isLoading: isValidationLoading } = useQuery({
    queryKey: ["validateYMKey", apiKeyDebounced],
    queryFn: () => validateYMKey(apiKeyDebounced),
    retry: 1,
    enabled: Boolean(apiKeyDebounced),
  });

  const stores = validationData?.campaigns ?? [];

  const saveStoreMutation = useMutation({
    mutationFn: createYMSeller,
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      if (![
        "verification-ticket-invalid-code",
        "verification-ticket-expired",
        "company-already-linked-to-other-account",
      ].find(v => v === error?.response?.data?.code)) {
        onError();
      }
    },
  });

  useEffect(() => {
    onStatusChange({
      disabled: step === steps.CONFIRM ? confirmState.disabled : Boolean(!isValid || isValidationLoading || !validationData?.isSuccess),
      isLoading: Boolean(sendLinkMutation.isLoading || saveStoreMutation.isLoading || confirmState.loading),
      title: (step === steps.CONFIRM) ? t("storesPage.addShopModal.confirmBtn") : "",
    });
  }, [isValid, isValidationLoading, validationData?.isSuccess, sendLinkMutation.isLoading, saveStoreMutation.isLoading, step, confirmState.disabled, confirmState.loading])

  const onSubmit = (form: YMFormType) => {
    if (step === steps.EMAIL) {
      const isKnownEmail = knownEmails.find(v => (v === form.email));
      if (isKnownEmail) {
        saveStoreMutation.mutate({
          email: form.email,
          apiKey: form.apiKey,
          ymCampaignId: form.ymCampaignId,
          code: null,
          ticketId: null,
          partnerId: form.partnerId || undefined,
        });
      } else {
        sendLinkMutation.mutate({
          email: form.email,
        }, {
          onSuccess: ({ ticketId }) => {
            setTicket(ticketId);
            setStep(steps.CONFIRM);
          },
        })
      }
    } else if (step === steps.CONFIRM) {
      saveStoreMutation.mutate({
        email: form.email,
        apiKey: form.apiKey,
        ymCampaignId: form.ymCampaignId,
        code: pin,
        ticketId: ticket,
        partnerId: form.partnerId || undefined,
      });
    }
  };

  const submit = handleSubmit(onSubmit);
  const back = () => setStep(steps.EMAIL);

  useImperativeHandle(formRef, () => ({
    submit,
    back: step === steps.CONFIRM ? back : undefined,
  }));

  if (step === steps.CONFIRM) {
    const [email] = getValues(["email"]);
    return (
      <ConfirmEmail
        email={email}
        pin={pin}
        setPin={setPin}
        setTicket={setTicket}
        setConfirmState={setConfirmState}
        onSubmit={submit}
        error={saveStoreMutation.isError ? saveStoreMutation.error : undefined}
      />
    )
  }

  return (
    <form
      className={styles.form}
      onSubmit={submit}
    >
      <SelectContainer<ICampaigns, YMFormType>
        control={control}
        name="ymCampaignId"
        title={t("storesPage.editShopModal.storeInput")}
        placeholder={t("storesPage.editShopModal.storeInputPlaceholder")}
        renderSelectedItem={StoresSelectValue}
        items={stores}
        keyExtractor={(item: ICampaigns) => item.id}
        rules={{
          required: t("defaultFormErrors.required"),
        }}
        disabled
      />
      <TextInputContainer
        control={control}
        name="apiKey"
        title={t('storesPage.editShopModal.apiKeyInput')}
        placeholder="API key"
        rules={{
          required: t("defaultFormErrors.required"),
        }}
        type="text"
        hasError={Boolean(apiKey && !validationData?.isSuccess)}
        error={t(
          `storesPage.addShopModal.errors.${validationData?.error}`,
          { defaultValue: t("defaultFormErrors.default") },
        )}
        maxLength={999}
      />
      <TextInputContainer
        control={control}
        name="email"
        title={(<span className={styles.emailTitle}>
          <span>{t('storesPage.editShopModal.emailInput')}</span>
          <Tooltip
            action={
              <IconButton
                className={styles.infoIcon}
                icon={InfoIcon}
                onClick={() => {}}
                size={IconButtonSize.L}
                type={IconButtonType.ICON}
              />
            }
            position="top"
            text={t('storesPage.editShopModal.emailTooltip')}
          />
        </span>)}
        placeholder="Email"
        rules={{ required: t("defaultFormErrors.required") }}
        type="email"
        hasError={sendLinkMutation.isError}
        error={getErrorMessage({ t, error: saveStoreMutation.error || sendLinkMutation.error })}
      />
      <TextInputContainer
        control={control}
        name="partnerId"
        title={t('storesPage.addShopModal.partnerId')}
        placeholder="Partner ID"
        rules={{
          required: t("defaultFormErrors.required"),
        }}
        type="text"
        hasError={Boolean(getPartnerIdQuery?.data && !getPartnerIdQuery?.data?.partnerId)}
        error={t("storesPage.addShopModal.partnerIdNotFound")}
        //maxLength={10}
        disabled={Boolean(
          getPartnerIdQuery?.isLoading ||
          !getPartnerIdQuery?.data ||
          getPartnerIdQuery?.data.partnerId
        )}
        button={getPartnerIdQuery?.data?.partnerId ? undefined : {
          title: t("storesPage.addShopModal.partnerIdInfLink"),
          onClick: () => setPartnerInfoOpen(true),
        }}
      />

      {isPartnerInfoOpen ? (
        <PartnerIdInfoModal onClose={() => setPartnerInfoOpen(false)} />
      ) : null}
    </form>
  )
};

export default YMForm;
